import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const VOICE_KEYS = [
  { key: 'realtime_provider',         label: 'Proveedor (openai|gemini)',  type: 'text' },
  { key: 'openai_realtime_model',     label: 'Modelo OpenAI',              type: 'text' },
  { key: 'openai_realtime_voice',     label: 'Voz OpenAI',                 type: 'text' },
  { key: 'gemini_realtime_model',     label: 'Modelo Gemini',              type: 'text' },
  { key: 'gemini_realtime_voice',     label: 'Voz Gemini',                 type: 'text' },
  { key: 'realtime_enabled',          label: 'Realtime habilitado (1/0)',  type: 'text' },
  { key: 'ai_customer_enabled',       label: 'IA del cliente activa (1/0)', type: 'text' },
];
const PROMPT_KEYS = [
  { key: 'realtime_instructions',           label: 'Instrucciones base (identidad, protocolo)' },
  { key: 'bot_hard_rules',                  label: 'Hard rules (protocolo inviolable)' },
  { key: 'owner_mode_instructions',         label: 'Modo propietario (instrucciones específicas)' },
  { key: 'inbound_instructions_general',    label: 'Inbound — general' },
  { key: 'outbound_instructions_general',   label: 'Outbound — general' },
  { key: 'service_line_principal_instructions', label: 'Línea principal — instrucciones' },
];

const Minerva = () => {
  const modal = useModal();
  const [tab, setTab] = useState('voz');
  const [settings, setSettings] = useState({});
  const [draft, setDraft] = useState({});
  const [numbers, setNumbers] = useState([]);
  const [showNumberModal, setShowNumberModal] = useState(false);
  const [editingNumber, setEditingNumber] = useState(null);
  const [numberForm, setNumberForm] = useState({ phoneNumber: '', lineType: 'principal', inboundEnabled: true, outboundEnabled: false });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useViewMode('minerva');
  const fl = useFieldLayout('minerva', viewMode);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [s, n] = await Promise.all([
        api.get('/minerva-settings'),
        api.get('/telnyx-numbers'),
      ]);
      const map = {};
      (s.data || []).forEach((row) => { map[row.key] = row.value; });
      setSettings(map);
      setDraft(map);
      setNumbers(Array.isArray(n.data) ? n.data : []);
    } catch (e) {
      modal.error('Error cargando configuración: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    try {
      const items = Object.entries(draft)
        .filter(([k, v]) => v !== settings[k])
        .map(([key, value]) => ({ key, value }));
      if (items.length === 0) {
        modal.success('Sin cambios');
        return;
      }
      await api.put('/minerva-settings', { items });
      setSettings({ ...settings, ...Object.fromEntries(items.map((i) => [i.key, i.value])) });
      modal.success(`${items.length} setting(s) guardado(s)`);
    } catch (e) {
      modal.error(e.response?.data?.message || 'Error guardando');
    }
  };

  const setDraftKey = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // Telnyx numbers
  const openNewNumber = () => {
    setEditingNumber(null);
    setNumberForm({ phoneNumber: '', lineType: 'principal', inboundEnabled: true, outboundEnabled: false });
    setShowNumberModal(true);
  };
  const openEditNumber = (n) => {
    setEditingNumber(n);
    setNumberForm({ phoneNumber: n.phoneNumber, lineType: n.lineType, inboundEnabled: n.inboundEnabled, outboundEnabled: n.outboundEnabled });
    setShowNumberModal(true);
  };
  const saveNumber = async (e) => {
    e.preventDefault();
    try {
      if (editingNumber) {
        await api.put(`/telnyx-numbers/${editingNumber.id}`, numberForm);
      } else {
        await api.post('/telnyx-numbers', numberForm);
      }
      await loadAll();
      setShowNumberModal(false);
    } catch (err) {
      modal.error(err.response?.data?.message || 'Error guardando número');
    }
  };
  const deleteNumber = (id) => {
    modal.confirm('¿Borrar este número?', async () => {
      try {
        await api.delete(`/telnyx-numbers/${id}`);
        await loadAll();
      } catch (err) {
        modal.error('Error borrando');
      }
    });
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando…</div>;
  }

  const columns = [
    { key: 'phoneNumber', header: 'Número', className: 'dt-strong', fixed: true, render: (n) => n.phoneNumber },
    { key: 'lineType', header: 'Tipo de línea', render: (n) => n.lineType },
    { key: 'inboundEnabled', header: 'Entrante', render: (n) => (
      <span className={`badge ${n.inboundEnabled ? 'badge-success' : 'badge-gray'}`}>
        {n.inboundEnabled ? 'inbound' : 'sin inbound'}
      </span>
    ) },
    { key: 'outboundEnabled', header: 'Saliente', render: (n) => (n.outboundEnabled ? 'sí' : 'no') },
    { key: 'actions', header: '', className: 'col-actions', render: (n) => (
      <div className="dt-actions">
        <button className="btn-icon" onClick={() => openEditNumber(n)} title="Editar"><Edit2 size={16} /></button>
        <button className="btn-icon" onClick={() => deleteNumber(n.id)} title="Borrar" style={{ color: '#ef4444' }}><Trash2 size={16} /></button>
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Configuración de Minerva</h1>
          <p className="page-subtitle">Voz, IA y telefonía. Aquí no hay datos de negocio.</p>
        </div>
        {tab !== 'numeros' && (
          <button className="btn btn-primary" onClick={saveDraft}>
            <Save size={18} /> Guardar cambios
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn ${tab === 'voz' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('voz')}>Voz / IA</button>
          <button className={`btn ${tab === 'prompt' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('prompt')}>Instrucciones del bot</button>
          <button className={`btn ${tab === 'numeros' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('numeros')}>Números Telnyx</button>
        </div>
      </div>

      {tab === 'voz' && (
        <div className="card">
          <div style={{ display: 'grid', gap: 12 }}>
            {VOICE_KEYS.map(({ key, label }) => (
              <div className="form-group" key={key} style={{ margin: 0 }}>
                <label className="form-label">{label}</label>
                <input className="form-input" value={draft[key] || ''} onChange={(e) => setDraftKey(key, e.target.value)} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>{key}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'prompt' && (
        <div className="card">
          <div style={{ display: 'grid', gap: 16 }}>
            {PROMPT_KEYS.map(({ key, label }) => (
              <div className="form-group" key={key} style={{ margin: 0 }}>
                <label className="form-label">{label}</label>
                <textarea className="form-input" rows={6} value={draft[key] || ''} onChange={(e) => setDraftKey(key, e.target.value)} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>{key}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'numeros' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
            <button className="btn btn-primary" onClick={openNewNumber}><Plus size={18} /> Nuevo número</button>
          </div>
          {numbers.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
              No hay números Telnyx configurados.
            </div>
          ) : viewMode === 'table' ? (
            <DataTable columns={fl.orderColumns(columns)} rows={numbers} hiddenKeys={fl.hidden} />
          ) : (
            <div className="items-grid">
              {numbers.map((n) => (
                <div className="item-card" key={n.id}>
                  <div className="item-card-header">
                    <div className="item-card-avatar">☎</div>
                    <div style={{ flex: 1 }}>
                      <div className="item-card-title">{n.phoneNumber}</div>
                      {!fl.isHidden('lineType') && <div className="item-card-subtitle">{n.lineType}</div>}
                    </div>
                    {!fl.isHidden('inboundEnabled') && (
                      <span className={`badge ${n.inboundEnabled ? 'badge-success' : 'badge-gray'}`}>
                        {n.inboundEnabled ? 'inbound' : 'sin inbound'}
                      </span>
                    )}
                  </div>
                  <div className="item-card-body">
                    {!fl.isHidden('outboundEnabled') && (
                      <div className="item-card-row" style={fl.cardStyle('outboundEnabled')}><span className="item-label">Saliente</span><span>{n.outboundEnabled ? 'sí' : 'no'}</span></div>
                    )}
                  </div>
                  <div className="item-card-footer">
                    <div className="item-card-actions">
                      <button className="btn-icon" onClick={() => openEditNumber(n)} title="Editar"><Edit2 size={16} /></button>
                      <button className="btn-icon" onClick={() => deleteNumber(n.id)} title="Borrar" style={{ color: '#ef4444' }}><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showNumberModal && (
        <div className="modal-overlay" onClick={() => setShowNumberModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingNumber ? 'Editar número' : 'Nuevo número Telnyx'}</h2>
              <button className="btn-icon" onClick={() => setShowNumberModal(false)}>&times;</button>
            </div>
            <form onSubmit={saveNumber}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Número (E.164) *</label>
                  <input className="form-input" required value={numberForm.phoneNumber}
                    onChange={(e) => setNumberForm({ ...numberForm, phoneNumber: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo de línea</label>
                  <input className="form-input" value={numberForm.lineType}
                    onChange={(e) => setNumberForm({ ...numberForm, lineType: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={numberForm.inboundEnabled}
                      onChange={(e) => setNumberForm({ ...numberForm, inboundEnabled: e.target.checked })} />
                    Inbound habilitado
                  </label>
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={numberForm.outboundEnabled}
                      onChange={(e) => setNumberForm({ ...numberForm, outboundEnabled: e.target.checked })} />
                    Outbound habilitado
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowNumberModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Minerva;
